import {TENANT_BASE_URL} from "../../../common/Config/UrlHelper";
import DatatableHelperMixin from "../../../common/Mixin/Global/DatatableHelperMixin";
import {formatDateToLocal} from "../../../common/Helper/Support/DateTimeHelper";
import HelperMixin from "../../../common/Mixin/Global/HelperMixin";
import {formatCurrency, numberFormatter} from "../../../common/Helper/Support/SettingsHelper";
import {axiosGet, axiosPatch} from "../../../common/Helper/AxiosHelper";
import { SELECTABLE_DEPARTMENT, TENANT_SELECTABLE_USER } from "../../Config/ApiUrl";

export default {
    mixins: [HelperMixin, DatatableHelperMixin],
    data() {
        return {
            reorderProcessing: false,
            options: {
                name             : this.$t('finance_transfers'),
                url              : `${TENANT_BASE_URL}app/finance-transfers`,
                showHeader       : true,
                showCount        : true,
                tableShadow      : false,
                tablePaddingClass: 'pt-primary',
                columns          : [
                    {
                        title    : '',
                        type     : 'custom-html',
                        key      : 'display_order',
                        isVisible: this.$can('update_finance_transfers'),
                        tdClassName: 'reorder-handle',
                        modifier : () => '<span class="drag-icon text-muted">&#9776;</span>'
                    },
                    {
                        title    : this.$t('actions'),
                        type     : 'action',
                        key      : 'id',
                        isVisible: true
                    },
                    {
                        title    : this.$t('source_account'),
                        type     : 'custom-html',
                        key      : 'source_account',
                        isVisible: true,
                        modifier : (value) => {
                            return value?.name;
                        }
                    },
                    {
                        title    : this.$t('target_account'),
                        type     : 'custom-html',
                        key      : 'target_account',
                        isVisible: true,
                        modifier : (value) => {
                            return value?.name;
                        }
                    },
                    {
                        title    : this.$t('amount'),
                        type     : 'custom-html',
                        key      : 'amount',
                        isVisible: true,
                        modifier : (value) => {
                            return formatCurrency(numberFormatter(Number(value)));
                        }
                    },
                    {
                        title    : this.$t('date'),
                        type     : 'custom-html',
                        key      : 'process_date',
                        isVisible: true,
                        modifier : (value) => {
                            return value ? formatDateToLocal(value) : '';
                        }
                    },
                    {
                        title    : this.$t('remark'),
                        type     : 'text',
                        key      : 'remark',
                        isVisible: true,
                    },
                    {
                        title    : this.$t('created_by'),
                        type     : 'custom-html',
                        key      : 'created_by',
                        isVisible: true,
                        modifier : (value) => {
                            return value ? value.full_name : '';
                        }
                    },
                    {
                        title    : this.$t('created_at'),
                        type     : 'custom-html',
                        key      : 'created_at',
                        isVisible: true,
                        modifier : (value) => {
                            return value ? formatDateToLocal(value) : '';
                        }
                    },
                ],
                filters          : [
                    {
                        title         : this.$t('search'),
                        type          : "text",
                        key           : "search",
                        placeholder   : this.$t('search')
                    },
                    {
                        title: this.$t('department'),
                        type: "multi-select-filter",
                        key: "department_id",
                        option: [],
                        listValueField: 'name',
                    },
                    {
                        title: this.$t('created_by'),
                        type: "multi-select-filter",
                        key: "created_by",
                        option: [],
                        listValueField: 'employee_name',
                    },
                    {
                        title : this.$t('date'),
                        type : "range-picker",
                        key   : "date",
                        option: ["today", "thisMonth", "last7Days", "thisYear"]
                    },
                    {
                        title : this.$t('source_account'),
                        type  : "multi-select-filter",
                        key   : "source_account",
                        option: [],
                        listValueField: 'name',
                    },
                    {
                        title : this.$t('target_account'),
                        type  : "multi-select-filter",
                        key   : "target_account",
                        option: [],
                        listValueField: 'name',
                    },
                ],
                paginationType   : "pagination",
                responsive       : true,
                rowLimit         : 10,
                showAction       : true,
                orderBy          : 'desc',
                actionType       : 'default',
                isRowDragAble    : this.$can('update_finance_transfers'),
                dragCompleteHandler: (event, rows) => this.handleReorderComplete(event, rows),
                actions          : [
                    {
                        title   : this.$t('edit'),
                        icon    : 'edit',
                        type    : 'modal',
                        modifier: row => this.$can('update_finance_transfers')
                    },
                    {
                        title    : this.$t('delete'),
                        icon     : 'trash-2',
                        type     : 'modal',
                        component: 'app-confirmation-modal',
                        modalId  : 'app-confirmation-modal',
                        url      : `${TENANT_BASE_URL}app/finance-transfers/`,
                        name     : 'delete',
                        modifier : row => this.$can('delete_finance_transfers')
                    }
                ],
            }
        }
    },
    methods: {
        handleReorderComplete(event, rows = []) {
            if (!this.$can('update_finance_transfers') || this.reorderProcessing) {
                return;
            }

            if (!Array.isArray(rows) || !rows.length) {
                return;
            }

            const oldIndex = event.oldIndex;
            const newIndex = event.newIndex;

            // Rebuild the visible order from current display_order before applying the drag move.
            const sortedRows = [...rows]
                .filter(row => row != null && row.display_order != null)
                .sort((a, b) => {
                    if (b.display_order !== a.display_order) {
                        return b.display_order - a.display_order;
                    }

                    return b.id - a.id;
                });

            // Validate indices
            if (oldIndex < 0 || oldIndex >= sortedRows.length ||
                newIndex < 0 || newIndex > sortedRows.length) {
                console.error('Invalid drag indices:', {oldIndex, newIndex, arrayLength: sortedRows.length});
                return;
            }

            // Manually perform the move operation
            const [movedItem] = sortedRows.splice(oldIndex, 1);
            if (!movedItem) {
                console.error('Could not find item to move at index:', oldIndex);
                return;
            }
            sortedRows.splice(newIndex, 0, movedItem);

            // Keep the current page's order values and reassign them to the new visual order.
            const pageDisplayOrders = sortedRows
                .map(row => Number(row.display_order))
                .sort((a, b) => b - a);

            const orders = sortedRows
                .filter(row => row != null)
                .map((row, index) => ({
                    id: row.id,
                    display_order: pageDisplayOrders[index]
                }));

            this.reorderProcessing = true;
            axiosPatch(`${TENANT_BASE_URL}app/finance-transfers/reorder`, {orders})
                .then(({data}) => {
                    this.$toastr.s(data.message);
                    this.$hub.$emit('reload-finance-transfer-table', false);
                })
                .catch(error => {
                    if (error.response) {
                        this.toastException(error.response.data);
                    }
                })
                .finally(() => {
                    this.reorderProcessing = false;
                });
        }
    },
    mounted() {
        // Load options accounts for filters
        const fromAccountFilter = this.options.filters.find(item => item.key === 'source_account');
        const toAccountFilter = this.options.filters.find(item => item.key === 'target_account');
        axiosGet(`${TENANT_BASE_URL}app/finance-accounts?all=1`)
        .then(({data}) => {
            fromAccountFilter.option = data; 
            toAccountFilter.option = data;
        })
        .catch(error => this.$toastr.e(error.response.data.message));

        // Load options users for filters
        const createdByFilter = this.options.filters.find(fl => fl.key === 'created_by');
        axiosGet(TENANT_SELECTABLE_USER).then(({data}) => createdByFilter.option = data);

        
        // Load options departments for filters
        const departmentFilter = this.options.filters.find(fl => fl.key === 'department_id');
        axiosGet(SELECTABLE_DEPARTMENT).then(({data}) => departmentFilter.option = data);
    },
}
